import {
  Component,
  Inject,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { AddElementsComponent } from '../add-elements/add-elements.component';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import {
  combineLatest,
  map,
  Observable,
  of,
  startWith,
  Subscription,
  tap,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { Actions, ofType } from '@ngrx/effects';
import {
  AddBrandError,
  AddBrandSuccess,
} from 'src/app/features/brands/store/brands.actions';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-edit-basic-product-info',
  templateUrl: './edit-basic-product-info.component.html',
  styleUrls: ['./edit-basic-product-info.component.scss'],
})
export class EditBasicProductInfoComponent implements OnInit {
  @ViewChildren(MatAutocompleteTrigger)
  autocompleteTriggers!: QueryList<MatAutocompleteTrigger>;
  public Editor = ClassicEditor;
  private subscriptions: Subscription[] = [];
  form!: FormGroup;
  disableDate!: boolean;
  private imageCounter = 0;
  uploadedImage!: string;
  uploadedImages: any[] = [];
  defaultVariantAvatar: string =
    '../../../../../assets/images/uoload-image.jpeg';
  brands$: Observable<IBrand[]> = of([]);
  filteredBrands$!: Observable<IBrand[]>;
  productType!: string;
  brand_id!: number;
  brand: IBrand | null = null;;
  timestamp = new Date().getTime();
  cdnBaseUrl = environment.BASE_URL_API
  constructor(
    public dialogRef: MatDialogRef<EditBasicProductInfoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private actions$: Actions
  ) {
    this.brands$ = this.store.select((state) => [...state.brands.allBrands].sort((a, b) => a.name.localeCompare(b.name)));
    this.productType = this.data.productType;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      name: new FormControl(this.data?.name ?? '', Validators.required),
      status: new FormControl(this.data?.status ?? ''),
      description: new FormControl(this.data?.description ?? ''),
      brandControl: new FormControl(this.data?.brandControl ?? ''),
    });
    
    this.form.get('brandControl')?.valueChanges.subscribe(value => {
      if (typeof value === 'string' && value.trim() === '') {
        this.brand_id = 0
        this.brand = null
        this.form.get('brandControl')?.setValue(null, { emitEvent: false });
      }
    });
    const images = this.data?.images;
    let parsedImages = [];
    if(images.length > 0) {
      parsedImages = images.map((image: any) => {
        if(typeof image === 'string') {
          const imageObject = {
            id: this.imageCounter++, // take the attachment id
            preview: image,
            isDefault: false
          }

          return imageObject;
        } else {
          return image
        }
      })
    }

    if(this.data?.image && images.filter((image: any) => image.isDefault).length === 0) {
      parsedImages.push(
        {
          id: this.imageCounter++,
          preview: this.data?.image,
          isDefault: true
        }
      )
    }

    parsedImages.sort((a: any, b: any) => {
      if (b.isDefault !== a.isDefault) {
          return b.isDefault ? 1 : -1;
      }
      return a.id - b.id;
    });

    this.uploadedImages = parsedImages;
    console.log('Uploaded Images', this.uploadedImages)

    this.brand_id = this.data.brand_id;
    this.brand = this.data.brand;
    this.filteredBrands$ = combineLatest([
      this.brands$,
      this.form.get('brandControl')!.valueChanges.pipe(startWith('')),
    ]).pipe(map(([brands, input]) => this._filterStrings(brands, input)));

    const sub1 = this.actions$
      .pipe(
        ofType(AddBrandSuccess),
        tap((action) => {
          this.form.get('brandControl')?.setValue(action.brand.name);
          this.brand_id = action.brand.id;
        })
      )
      .subscribe();

    const sub2 = this.actions$
      .pipe(
        ofType(AddBrandError),
        tap(() => {
          this.form.get('brandControl')?.setValue('');
        })
      )
      .subscribe();

    this.subscriptions.push(sub1, sub2);
  }

  getImage(image: any): string{
    return !image.preview.startsWith('data:')  ? this.cdnBaseUrl + image.preview + '?' + this.timestamp : `data:'';base64, ${image.preview.split(',')[1]}`
  }

  removeImage(imageId: number) {
    const imageToRemove = this.uploadedImages.find(image => image.id === imageId);

    if (imageToRemove && imageToRemove.isDefault) {
      const newDefault = this.uploadedImages.find(image => image.id !== imageId && !image.isDefault);

      if (newDefault) {
        newDefault.isDefault = true;
      }
    }

    this.uploadedImages = this.uploadedImages.filter(image => image.id !== imageId);
  }

  removeAllImages() {
    this.uploadedImages = [];
  }

  getDefaultImage() {
    const defaultImage = this.uploadedImages.find(image => image.isDefault);
    return defaultImage ? defaultImage.preview.split(',')[1] : undefined;
  }

  setDefault(imageId: number) {
    this.uploadedImages = this.uploadedImages.map((image) => {
      image.isDefault = image.id === imageId ? true : false;

      return image;
    });
  }

  private _filterStrings(strings: any[], value: string): any[] {
    const filterValue = this._normalizeValue(value);
    return strings.filter((string) =>
      this._normalizeValue(string.name)?.includes(filterValue!)
    );
  }
  private _normalizeValue(value: string | undefined): string | undefined {
    return value?.toLowerCase().replace(/\s/g, '');
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.add('drag-over');
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');

    const files = event.dataTransfer?.files;
    if (files) {
      this.processFiles(files);
    }
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files) {
      this.processFiles(files);
    }
  }

  processFiles(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        const previewData = e.target?.result as string;
        console.log('On Upload', this.uploadedImages)
        const hasDefault = this.uploadedImages.filter((image: any) => image.isDefault).length

        this.uploadedImages.push({
          id: this.imageCounter++,
          file,
          preview: previewData,
          isDefault: hasDefault ? false : true,
        });

        this.uploadedImages.sort((a, b) => {
            if (b.isDefault !== a.isDefault) {
                return b.isDefault ? 1 : -1;
            }
            return a.id - b.id;
        });
      };
      reader.readAsDataURL(file);
    }
  }

  cancelImage() {
    this.uploadedImage = '';
  }

  onBrandSelected(brand: IBrand) {
    if (brand) {
      this.brand_id = brand.id;
      this.brand = brand;
    }
  }

  addNewElement(newElementName: string, type: string, index?: number) {
    const dialogRef = this.dialog.open(AddElementsComponent, {
      data: {
        name: newElementName,
        type: type,
        index: index,
      },
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe(() => {
      this.autocompleteTriggers.forEach((trigger) => trigger.closePanel());
    });
  }

  async save(): Promise<boolean | void> {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return false;
    }

    this.uploadedImages.sort((a, b) => {
        if (b.isDefault !== a.isDefault) {
            return b.isDefault ? 1 : -1;
        }
        return a.id - b.id;
    });

    const payload: any = {
      name: this.form.get('name')?.value,
      description: this.form.get('description')?.value,
      status: this.form.get('status')?.value,
      brandControl: this.form.get('brandControl')?.value,
      brand_id: this.brand_id,
      brand: this.brand,
      images: this.uploadedImages
    }

    this.dialogRef.close(payload);
  }

  closed() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
